import { Formik, FieldArray } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import i18next from 'i18next';
import PageHeader from '../../components/Shared/PageHeader'
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { getCurrentDateTime } from '../Transactions/CreateTransaction';
import TextInput from '../../components/Shared/TextInput';
import { useDispatch, useSelector } from 'react-redux';
import CustomSearchInput from '../../components/Shared/CustomSearchInput';
import SharedButton from '../../components/Shared/SharedButton';
import {
  CreatePaymentVoucherApi,
  getBankAccounts,
  getCashAccounts,
  updatePaymentVoucher
} from '../../store/slices/VoucherSlice';
import { Calendar } from 'primereact/calendar';
import { FaCheckDouble } from 'react-icons/fa6';

function CreatePaymentVoucher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { costCenters } = useSelector(state => state.costCenter);
  const { Accounts } = useSelector(state => state.voucher);
  const location = useLocation();
  const editData = location?.state?.data;
  const lang = i18next.language;
  const [vType, setVType] = useState('');
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getCashAccounts());
  }, [dispatch]);

  const initialValues = {
    voucher_type: editData ? editData?.voucher_type : 'CPV',
    date: editData?.date ? new Date(editData.date) : new Date(),
    total_amount: editData ? editData?.total_amount : 0,
    cost_center_id: editData ? editData?.cost_center_id : null,
    created_by: editData ? editData?.created_by : 34,
    details: editData
      ? editData?.details.map(detail => ({
        account_id: detail?.account_id,
        amount: detail?.amount,
        description: detail?.description
      }))
      : [
        {
          account_id: null,
          amount: 0,
          description: ''
        }
      ]
  };

  const validationSchema = Yup.object().shape({
    voucher_type: Yup.string().required(t('required')),
    date: Yup.mixed().required(t('required')),
    total_amount: Yup.number()
      .required(t('required'))
      .test('match-total', 'Total amount must be equal to the sum of details amounts', function (value) {
        const { details } = this.parent;
        const sumDetailsAmount = details.reduce((sum, detail) => sum + detail.amount, 0);
        return value === sumDetailsAmount;
      }),
    cost_center_id: Yup.number().nullable().required(t('required')),
    details: Yup.array().of(
      Yup.object().shape({
        account_id: Yup.number().nullable().required(t('required')),
        amount: Yup.number().required(t('required')).min(1, t('amount_greator_0')),
        description: Yup.string().nullable()
      })
    )
  });

  const listCostCentervalues = costCenters?.data
    ? Object?.entries(costCenters?.data || [])?.map(([id, costCenter]) => ({
      value: Number(costCenter?.id),
      label: lang === 'ar' ? costCenter?.name_ar : costCenter?.name_en
    }))
    : [];
  const listAcountvalues = costCenters?.data
    ? Object?.entries(Accounts?.data || [])?.map(([id, acount]) => ({
      value: Number(acount?.code),
      label: lang === 'ar' ? acount?.name_ar : acount?.name_en
    }))
    : [];

  useEffect(() => {
    if (vType === 'CPV') {
      dispatch(getCashAccounts());
    } else if (vType === 'BPV') {
      dispatch(getBankAccounts());
    }
  }, [vType]);
  return (
    <Formik
      validationSchema={validationSchema}
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={values => {
        const formattedValues = {
          ...values,
          date: values.date instanceof Date
            ? values.date.toISOString().slice(0, 19).replace('T', ' ')
            : values.date
        };
        if (editData) {
          dispatch(updatePaymentVoucher({ id: editData?.id, data: formattedValues })).then(() => {
            navigate('/manager/payment/voucher');
          });
        } else {
          dispatch(CreatePaymentVoucherApi(formattedValues)).then(() => {
            navigate('/manager/payment/voucher');
          });
        }
      }}
    >
      {props => (
        <form onSubmit={props.handleSubmit}>
          <div className=' w-full gap-2'>
            <PageHeader title={t('create_payment_voucher')} backPath={'/manager/payment/voucher'} />

            <div className='grid grid-cols-4 gap-2 mb-2'>
              <div className='flex flex-col gap-1 w-full'>
                <label className='text-base font-medium'>{t('voucher_type')}</label>
                <div className='flex w-full'>
                  <button
                    type='button'
                    onClick={() => {
                      props.setFieldValue('voucher_type', 'CPV');
                      setVType('CPV');
                    }}
                    className={`w-[50%] border !p-1.5 transition-all rounded-tl-md rounded-bl-md !border-r-0 ${props.values.voucher_type === 'CPV'
                      ? '!bg-primary !border-primary text-white'
                      : '!bg-gray-100 !border-white text-primary'
                      }`}
                  >
                    CPV
                  </button>
                  <button
                    type='button'
                    onClick={() => {
                      props.setFieldValue('voucher_type', 'BPV');
                      setVType('BPV');
                    }}
                    className={`w-[50%] border !p-1.5 !border-l-0 transition-all rounded-tr-md rounded-br-md ${props.values.voucher_type === 'BPV'
                      ? '!bg-primary !border-primary text-white'
                      : '!bg-gray-100 !border-white text-primary'
                      }`}
                  >
                    BPV
                  </button>
                </div>
              </div>

              <div className='flex flex-col gap-1 w-full'>
                <label htmlFor='date' className='input-label'>{t('date')}</label>
                <Calendar
                  id='date'
                  value={props.values.date}
                  onChange={(e) => props.setFieldValue('date', e.value)}
                  showTime
                  hourFormat="24"
                  dateFormat="dd-mm-yy"
                  placeholder={`${t("enter")} ${t("date")}`}
                  className='w-full'
                  pt={{
                    input: {
                      root: {
                        className: '!w-full !h-[30px] !p-1.5 rounded-md !bg-gray-100 border hover:border-primary focus:!outline-none focus:!shadow-none focus:!ring-0 !outline-none !shadow-none'
                      }
                    }
                  }}
                />
                {props.errors.date && props.touched.date && (
                  <div className="text-red-500 text-xs">{props.errors.date}</div>
                )}
              </div>
              <TextInput
                type='number'
                label={t('total_amount')}
                name='total_amount'
                placeholder={`${t('enter')} ${t('amount')}`}
                error={props.touched.total_amount && props.errors.total_amount}
              />

              {/* Cost Center Search */}
              <CustomSearchInput
                label={t('costCenter')}
                className='!w-full'
                name='cost_center_id'
                options={listCostCentervalues}
                placeholder={`${t('select')} ${t('costCenter')}`}
              />
            </div>

            <div>
              <label className='text-[18px] font-medium'>{t('details')}</label>
              <FieldArray
                name='details'
                render={arrayHelpers => (
                  <div className='!mt-1'>
                    {props?.values?.details?.map((detail, index) => (
                      <div className='grid grid-cols-5 gap-2 mb-2' key={index}>
                        <CustomSearchInput
                          label={t('account')}
                          className='!w-full'
                          name={`details.${index}.account_id`}
                          options={listAcountvalues}
                          placeholder={`${t('select')} ${t('account')}`}
                        />
                        <TextInput
                          type='number'
                          label={t('amount')}
                          name={`details.${index}.amount`}
                          placeholder={`${t('enter')} ${t('amount')}`}
                          error={props.touched.details?.[index]?.amount && props.errors.details?.[index]?.amount}
                        />
                        <TextInput
                          type='text'
                          label={t('description')}
                          name={`details.${index}.description`}
                          placeholder={`${t('enter')} ${t('description')}`}
                          onKeyDown={e => {
                            if (e.key === 'Tab' && !e.shiftKey && index === props?.values?.details.length - 1) {
                              e.preventDefault();
                              arrayHelpers.push({
                                account_id: null,
                                amount: 0,
                                description: ''
                              });
                            }
                          }}
                        />

                        {index > 0 && (
                          <div className='flex  items-end '>
                            <span className='h-[30px] w-full  '>
                              <RiDeleteBin5Line
                                size={20}
                                className='cursor-pointer text-red-500'
                                onClick={() => arrayHelpers.remove(index)}
                              />
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                    <SharedButton
                      icon='pi pi-plus'
                      label={t('add_detail')}
                      className='!p-1.5 !min-w-14 bg-primary'
                      type='button'
                      onClick={() =>
                        arrayHelpers.push({
                          account_id: null,
                          amount: 0,
                          description: ''
                        })
                      }
                    />
                  </div>
                )}
              />
            </div>

            <div className='mt-4 flex justify-center'>
              <SharedButton
                icon={<FaCheckDouble size={12} className='me-2' />}
                label={editData ? t('Update') : t('Submit')}
                className='!p-1.5 !min-w-14 bg-primary'
                type='submit'
              />
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}

export default CreatePaymentVoucher;
